import { AnchorButton, H1, Text } from '@payaca/ambrosia';

import useGetCanBulkExport from '@/api/queries/me/useGetCanBulkExport';
import useGetMyAccountFeatures from '@/api/queries/me/useGetMyAccountFeatures';
import { usePermissions } from '@/hooks/usePermissions';
import { useCanUpdateAccount } from '@/hooks/useCanUpdateAccount';
import CompanySettingsAccreditations from '@/ui/components/companySettings/CompanySettingsAccreditations';
import CompanySettingsBranding from '@/ui/components/companySettings/CompanySettingsBranding';
import CompanySettingsBusinessDetails from '@/ui/components/companySettings/CompanySettingsBusinessDetails';
import CompanySettingsChatAgent from '@/ui/components/companySettings/CompanySettingsChatAgent';
import CompanySettingsCis from '@/ui/components/companySettings/CompanySettingsCis';
import { CompanySettingsCustomFieldGroup } from '@/ui/components/companySettings/CompanySettingsCustomFieldGroup';
import CompanySettingsDataExport from '@/ui/components/companySettings/CompanySettingsDataExport';
import CompanySettingsEmailCustomisation from '@/ui/components/companySettings/CompanySettingsEmailCustomisation';
import CompanySettingsMessaging from '@/ui/components/companySettings/CompanySettingsMessaging';
import CompanySettingsPayments from '@/ui/components/companySettings/CompanySettingsPayments';
import CompanySettingsPrimaryPipeline from '@/ui/components/companySettings/CompanySettingsPrimaryPipeline';
import CompanySettingsProposalsInvoices from '@/ui/components/companySettings/CompanySettingsProposalsInvoices';
import CompanySettingsSchedule from '@/ui/components/companySettings/CompanySettingsSchedule';
import CompanySettingsTaxRates from '@/ui/components/companySettings/CompanySettingsTaxRates';
import CompanySettingsTerms from '@/ui/components/companySettings/CompanySettingsTerms';
import CompanySettingsTimeTracking from '@/ui/components/companySettings/CompanySettingsTimeTracking';
import CompanySettingsVatNumber from '@/ui/components/companySettings/CompanySettingsVatNumber';
import SettingsLayout from '@/ui/components/companySettings/SettingsLayout';
import { useSettingsNav } from '@/ui/components/companySettings/useSettingsNav';
import { CustomFieldGroup } from '@/ui/components/customFieldGroup/CustomFieldGroup';
import EntityTemplatesControl from '@/ui/components/entityTemplatesControl/EntityTemplatesControl';
import { ConnectionsSettings } from '@/ui/pages/connectionsPage/ConnectionsPage';
import { ReadyMadeLibrarySettings } from '@/ui/pages/documentBlueprintLibraryPage/DocumentBlueprintLibraryPage';
import { UsersSettings } from '@/ui/pages/listedUsersPage/ListedUsersPage';
import { DocumentTemplatesList } from '@/ui/pages/listedTemplatesPage/DocumentTemplatesList';
import TaskTemplatesList from '@/ui/pages/listedTemplatesPage/TaskTemplatesList';
import { TagsSettings } from '@/ui/pages/tagsPage/TagsPage';
import { FC, type JSX } from 'react';
import { Redirect, useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import {
  CompanySettingsFlow,
  CompanySettingsGlow,
} from '../../components/companySettings/CompanySettingsEstimates';
import CompanySettingsFieldServiceCustomFields from '../../components/companySettings/CompanySettingsFieldServiceCustomFields';
import CompanySettingsFieldServiceSuppliers from '../../components/companySettings/CompanySettingsFieldServiceSuppliers';
import CompanySettingsIntegrations from '../../components/companySettings/CompanySettingsIntegrations';
import CompanySettingsTabBase from '../../components/companySettings/CompanySettingsTabBase';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import './CompanySettingsPage.sass';

/**
 * Old single-level tab slugs mapped to their new `category/section` home, so
 * existing links and bookmarks keep resolving.
 */
const LEGACY_ROUTE_REDIRECTS: Record<string, string> = {
  'company-info': 'company/business-details',
  branding: 'company/brand-identity',
  'chat-agent': 'company/agent-dave',
  'getting-paid': 'sales-payments/payments',
  'tax-rates': 'sales-payments/tax',
  terms: 'sales-payments/terms',
  estimates: 'sales-payments/flow',
  'email-customisation': 'communications/email',
  schedule: 'operations/schedule',
  'field-service': 'operations/field-service',
  'custom-fields': 'custom-data/project-custom-fields',
  integrations: 'integrations-data/developer',
  'data-export': 'integrations-data/data-export',
};

const CompanySettingsPage: FC = (): JSX.Element => {
  const isAdmin = useCanUpdateAccount();
  const readOnly = !isAdmin;

  const categories = useSettingsNav();
  const { isLoading: permissionsLoading } = usePermissions();

  // The nav is permission/feature-gated on these queries; while they load, a
  // gated section legitimately isn't in `categories` yet, so we must not
  // redirect away from it (that would bounce a deep-link to the default tab).
  const { isLoading: bulkExportLoading } = useGetCanBulkExport();
  const { isLoading: featuresLoading } = useGetMyAccountFeatures();
  const gatesLoading =
    bulkExportLoading || featuresLoading || permissionsLoading;

  const {
    category: categoryParam,
    section: sectionParam,
    detail: detailParam,
  } = useParams<{
    category?: string;
    section?: string;
    detail?: string;
  }>();

  // `bare` sections carry their own ManageableItemsList chrome (action bar,
  // contextual headings), so the pane renders only the section heading above
  // them, without the tab base.
  const sectionPanes: Record<
    string,
    { content: () => JSX.Element; bare?: boolean }
  > = {
    'company/business-details': {
      content: () => <CompanySettingsBusinessDetails readOnly={readOnly} />,
    },
    'company/brand-identity': {
      content: () => <CompanySettingsBranding readOnly={readOnly} />,
    },
    'company/accreditations': {
      content: () => <CompanySettingsAccreditations readOnly={readOnly} />,
    },
    'company/company-fields': {
      content: () => <CustomFieldGroup type="account" />,
    },
    'company/agent-dave': { content: () => <CompanySettingsChatAgent /> },
    'team/users': { bare: true, content: () => <UsersSettings /> },
    'sales-payments/proposals-invoices': {
      content: () => <CompanySettingsProposalsInvoices readOnly={readOnly} />,
    },
    'sales-payments/proposal-templates': {
      content: () => <EntityTemplatesControl entityType="proposal" />,
    },
    'sales-payments/flow': { content: () => <CompanySettingsFlow /> },
    'sales-payments/glow': { content: () => <CompanySettingsGlow /> },
    'sales-payments/terms': {
      content: () => <CompanySettingsTerms readOnly={readOnly} />,
    },
    'sales-payments/payments': {
      content: () => <CompanySettingsPayments readOnly={readOnly} />,
    },
    'sales-payments/tax': {
      content: () => (
        <>
          <CompanySettingsVatNumber readOnly={readOnly} />
          <CompanySettingsTaxRates readOnly={readOnly} />
          <CompanySettingsCis readOnly={readOnly} />
        </>
      ),
    },
    'communications/email': {
      content: () => <CompanySettingsEmailCustomisation readOnly={readOnly} />,
    },
    'communications/messaging': {
      content: () => <CompanySettingsMessaging readOnly={readOnly} />,
    },
    'operations/schedule': {
      content: () => <CompanySettingsSchedule readOnly={readOnly} />,
    },
    'operations/primary-pipeline': {
      content: () => <CompanySettingsPrimaryPipeline readOnly={readOnly} />,
    },
    'operations/event-templates': {
      content: () => <EntityTemplatesControl entityType="scheduledEvent" />,
    },
    'operations/task-templates': { content: () => <TaskTemplatesList /> },
    'operations/field-service': {
      content: () => (
        <>
          <CompanySettingsFieldServiceSuppliers readOnly={readOnly} />
          <CompanySettingsFieldServiceCustomFields readOnly={readOnly} />
          <CompanySettingsTimeTracking readOnly={readOnly} />
        </>
      ),
    },
    'custom-data/project-custom-fields': {
      content: () => (
        <CompanySettingsCustomFieldGroup
          key="project"
          role="project"
          subHeading="Once created custom fieldsets can be set on each project page."
          headerAction={
            <AnchorButton
              as={Link}
              to="/companySettings/custom-data/ready-made-library"
              variant="secondary"
              size="sm"
            >
              Browse ready-made library
            </AnchorButton>
          }
        />
      ),
    },
    'custom-data/customer-custom-fields': {
      content: () => (
        <CompanySettingsCustomFieldGroup
          key="customer"
          role="customer"
          subHeading="Once created custom fieldsets can be set on each customer."
        />
      ),
    },
    'custom-data/account-custom-fields': {
      content: () => (
        <CompanySettingsCustomFieldGroup
          key="account"
          role="account"
          subHeading="Once created, company fieldsets can be filled in under Company > Company fields and used in document, email and proposal templates."
        />
      ),
    },
    'custom-data/tags': { bare: true, content: () => <TagsSettings /> },
    'custom-data/document-templates': {
      bare: true,
      content: () => <DocumentTemplatesList />,
    },
    'custom-data/ready-made-library': {
      bare: true,
      content: () => <ReadyMadeLibrarySettings />,
    },
    'integrations-data/connections': {
      // The trailing URL segment is the OAuth return service (connections/xero).
      content: () => <ConnectionsSettings service={detailParam} />,
    },
    'integrations-data/developer': {
      content: () => <CompanySettingsIntegrations />,
    },
    'integrations-data/data-export': {
      content: () => <CompanySettingsDataExport />,
    },
  };

  const { search } = useLocation();
  const withQuery = (path: string) => `${path}${search}`;

  const defaultRoute = categories[0]?.route ?? '/companySettings/company';

  const resolveRedirect = (): string | undefined => {
    if (!categoryParam) return defaultRoute;

    const category = categories.find((c) => c.slug === categoryParam);

    // Unknown category — try the legacy single-slug map, else default.
    if (!category) {
      const legacyTarget = LEGACY_ROUTE_REDIRECTS[categoryParam];
      const legacyRoute = legacyTarget
        ? `/companySettings/${legacyTarget}`
        : undefined;
      // Only honour the legacy target if its section is actually visible.
      if (
        legacyRoute &&
        categories.some((c) =>
          c.subSections.some((s) => s.route === legacyRoute)
        )
      ) {
        return legacyRoute;
      }
      return defaultRoute;
    }

    // The Estimates tab split into Flow and Glow. Old links land on Flow.
    if (categoryParam === 'sales-payments' && sectionParam === 'estimates') {
      const flow = category.subSections.find((sub) => sub.slug === 'flow');
      if (flow) return flow.route;
    }

    // Category with no/unknown section — land on its first section. This is
    // also the access control for deep links: a section the user cannot see
    // is absent from the permission-gated nav data, so it redirects away.
    const sectionExists = category.subSections.some(
      (sub) => sub.slug === sectionParam
    );
    if (!sectionParam || !sectionExists) return category.route;

    return undefined;
  };

  const redirect = gatesLoading ? undefined : resolveRedirect();
  if (redirect) {
    // Redirect defaults to replace semantics, matching a history.replace.
    return <Redirect to={withQuery(redirect)} />;
  }

  const activeCategory = categories.find((c) => c.slug === categoryParam);
  const activeSection = activeCategory?.subSections.find(
    (sub) => sub.slug === sectionParam
  );
  const pane = sectionPanes[`${categoryParam}/${sectionParam}`];

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        { label: 'Settings' },
        ...(activeCategory ? [{ label: activeCategory.label }] : []),
        ...(activeSection ? [{ label: activeSection.label }] : []),
      ]}
    >
      <SettingsLayout>
        {activeSection &&
          pane &&
          (pane.bare ? (
            <>
              <div className="p-4 pb-0">
                <H1>{activeSection.label}</H1>
              </div>
              {pane.content()}
            </>
          ) : (
            <CompanySettingsTabBase className="p-4 md:p-6">
              <div>
                <H1>{activeSection.label}</H1>
                {activeSection.description ? (
                  <Text level="supporting">{activeSection.description}</Text>
                ) : null}
              </div>
              {pane.content()}
            </CompanySettingsTabBase>
          ))}
      </SettingsLayout>
    </AuthenticatedPageWrapper>
  );
};

export default CompanySettingsPage;
